import { logger } from "../core/logger";
import { THEME_EVENTS, publish, subscribe } from "../../shared/events";
import { SECTION_REFRESH_RESOURCE_TYPE } from "../core/config";
import { HTMLUpdateUtility } from "../core/utils";

class ProductInfo extends HTMLElement {
  //quantityInput = undefined;
  //quantityForm = undefined;
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();

    //this.quantityInput = this.querySelector(".quantity__input");
  }

  connectedCallback() {
    this.initializeProductSwapUtility();

    this.onVariantChangeUnsubscriber = subscribe(
      THEME_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this),
    );

    this.initQuantityHandlers();
    this.dispatchEvent(
      new CustomEvent("product-info:loaded", { bubbles: true }),
    );
  }

  addPreProcessCallback(callback) {
    this.preProcessHtmlCallbacks.push(callback);
  }

  initQuantityHandlers() {
    if (!this.quantityInput || !this.quantityForm) return;

    this.setQuantityBoundries();
    if (!this.dataset.originalSection) {
      this.cartUpdateUnsubscriber = subscribe(
        THEME_EVENTS.cartUpdate,
        this.fetchQuantityRules.bind(this),
      );
    }
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  initializeProductSwapUtility() {
    this.preProcessHtmlCallbacks.push((html) =>
      html
        .querySelectorAll(".scroll-trigger")
        .forEach((element) => element.classList.add("scroll-trigger--cancel")),
    );
    this.postProcessHtmlCallbacks.push((newNode) => {
      window?.Shopify?.PaymentButton?.init();
      window?.ProductModel?.loadShopifyXR();
      publish(THEME_EVENTS.sectionRefreshed, {
        data: {
          sectionId: this.sectionId,
          resource: {
            type: SECTION_REFRESH_RESOURCE_TYPE.product,
            id: newNode.dataset.productId,
          },
        },
      });
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    console.log("[PDP TEST] option change", {
      target,
      targetId: target?.id,
      targetValue: target?.value,
      selectedOptionValues,
      productInfoContainsTarget: this.contains(event.target),
      productUrl: target?.dataset?.productUrl,
      pendingRequestUrl: this.pendingRequestUrl,
      datasetUrl: this.dataset.url,
    });

    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
    const shouldSwapProduct = this.dataset.url !== productUrl;
    const shouldFetchFullPage = !this.isFeaturedProduct && shouldSwapProduct;

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl)
        : this.handleUpdateProductInfo(productUrl),
    });
  }

  resetProductFormState() {
    const productForm = this.productForm;
    productForm?.toggleSubmitButton(true);
    productForm?.handleErrorMessage();
  }

  get isFeaturedProduct() {
    return this.dataset.sectionId.includes("featured_product");
  }

  get quantityInput() {
    return this.querySelector(".quantity__input");
  }

  get quantityForm() {
    return this.querySelector(".product-form__quantity");
  }

  handleSwapProduct(productUrl) {
    return (html) => {
      this.productModal?.remove();

      // Grab the selected variant from the new product info
      const productInfoNode = html.querySelector(
        `product-info[data-section-id="${this.sectionId}"]`,
      );
      logger.info("ProductInfo", "productInfoNode", productInfoNode);
      const variant = this.getSelectedVariant(productInfoNode);
      this.updateURL(productUrl, variant?.id);

      // If we are in an embedded context (quick add, featured product, etc), only swap product info.
      // Otherwise, refresh the entire page content and sibling sections.
      if (this.dataset.updateUrl === "false") {
        HTMLUpdateUtility.viewTransition(
          this,
          html.querySelector("product-info"),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks,
        );
      } else {
        document.querySelector("head title").innerHTML =
          html.querySelector("head title").innerHTML;

        HTMLUpdateUtility.viewTransition(
          document.querySelector("main"),
          html.querySelector("main"),
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks,
        );
      }
    };
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.pendingRequestUrl = requestUrl;

    console.log("[PDP TEST] requestUrl", requestUrl);

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.log("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(
      "variant-selects [data-selected-variant]",
    )?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
    const params = [];

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(",")}`);
    }

    return `${url}?${params.join("&")}`;
  }

  updateOptionValues(html) {
    const variantSelects = html.querySelector("variant-selects");
    if (variantSelects) {
      HTMLUpdateUtility.viewTransition(
        this.variantSelectors,
        variantSelects,
        this.preProcessHtmlCallbacks,
      );
    }
  }

  handleUpdateProductInfo(productUrl) {
    return (html) => {
      const variant = this.getSelectedVariant(html);

      console.log("[PDP TEST] handleUpdateProductInfo variant", {
        variant,
        variantId: variant?.id,
      });

      this.updateVariantImage(variant);
      this.updateThumbnails(html);

      this.pickupAvailability?.update(variant);
      this.updateOptionValues(html);
      this.updateURL(productUrl, variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      const updateSourceFromDestination = (
        id,
        shouldHide = (source) => false,
      ) => {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(
          `#${id}-${this.dataset.sectionId}`,
        );
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.classList.toggle("hidden", shouldHide(source));
        }
      };

      updateSourceFromDestination("Price");
      updateSourceFromDestination("Price-Abo");
      updateSourceFromDestination("Unit-Price");
      updateSourceFromDestination("Unit-Price-Abo");
      updateSourceFromDestination(
        "Inventory",
        ({ innerText }) => innerText === "",
      );
      updateSourceFromDestination("Volume");
      updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
        classList.contains("hidden"),
      );

      this.updateQuantityRules(this.sectionId, html);
      this.querySelector(
        `#Quantity-Rules-${this.dataset.sectionId}`,
      )?.classList.remove("hidden");
      this.querySelector(
        `#Volume-Note-${this.dataset.sectionId}`,
      )?.classList.remove("hidden");

      this.productForm?.toggleSubmitButton(
        html
          .getElementById(`ProductSubmitButton-${this.sectionId}`)
          ?.hasAttribute("disabled") ?? true,
        window.variantStrings.soldOut,
      );

      publish(THEME_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          html,
          variant,
        },
      });
    };
  }

  updateVariantInputs(variantId) {
    this.querySelectorAll(
      `#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`,
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = variantId ?? "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  updateURL(url, variantId) {
    this.querySelector("share-button")?.updateUrl(
      `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
    );

    if (this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${url}${variantId ? `?variant=${variantId}` : ""}`,
    );
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(
      true,
      window.variantStrings.unavailable,
    );

    const selectors = [
      "price",
      "Inventory",
      "Sku",
      "Price-Per-Item",
      "Volume-Note",
      "Volume",
      "Quantity-Rules",
    ]
      .map((id) => `#${id}-${this.dataset.sectionId}`)
      .join(", ");
    document
      .querySelectorAll(selectors)
      .forEach(({ classList }) => classList.add("hidden"));
  }

  updateVariantImage(variant) {
    if (!variant || !variant.featured_media) return;

    const mediaId = variant.featured_media.id;

    /* slider principal PDP */
    const images = this.querySelectorAll(".product__media-item--variant");
    images.forEach((img) => {
      img.classList.remove("is-active");
    });
    const activeImage = this.querySelector(
      `.product__media-item--variant[data-media-id="${mediaId}"]`,
    );
    if (activeImage) {
      activeImage.classList.add("is-active");
    }

    const productGallery = this.querySelector("product-media-gallery");
    productGallery?.productSwiper.update();
    productGallery?.goToVariantSlide();

    /* image quick add modal */
    const quickAddMedia = this.closest("quick-add-modal")?.querySelector(
      "[data-quick-add-media]",
    );

    if (!quickAddMedia) return;

    const quickAddImages = quickAddMedia.querySelectorAll(
      ".product__media-item--variant",
    );

    quickAddImages.forEach((img) => {
      img.classList.remove("is-active");
    });

    const quickAddActiveImage = quickAddMedia.querySelector(
      `.product__media-item--variant[data-media-id="${mediaId}"]`,
    );

    if (quickAddActiveImage) {
      quickAddActiveImage.classList.add("is-active");
    }
  }

  updateThumbnails(html) {
    const source = html.getElementById(`ThumbsWrapper-${this.sectionId}`);
    const destination = this.querySelector(
      `#ThumbsWrapper-${this.dataset.sectionId}`,
    );
    if (!source || !destination) return;

    destination.innerHTML = source.innerHTML;

    const productGallery = this.querySelector("product-media-gallery");
    const thumbsSwiper = productGallery?.thumbsSwiper;
    const productSwiper = productGallery?.productSwiper;

    if (thumbsSwiper) {
      thumbsSwiper.updateSlides();
      thumbsSwiper.update();
      thumbsSwiper.slideTo(0, 0);
    }

    if (productSwiper?.thumbs?.swiper) {
      productSwiper.thumbs.update();
    }

    destination
      .querySelectorAll(".swiper-slide-thumb-active")
      .forEach((el) => el.classList.remove("swiper-slide-thumb-active"));
    destination
      .querySelector(".swiper-slide")
      ?.classList.add("swiper-slide-thumb-active");
  }

  setQuantityBoundries() {
    const quantityInput = this.quantityInput;
    if (!quantityInput) return;

    const data = {
      cartQuantity: this.quantityInput.dataset.cartQuantity
        ? parseInt(this.quantityInput.dataset.cartQuantity)
        : 0,
      min: this.quantityInput.dataset.min
        ? parseInt(this.quantityInput.dataset.min)
        : 1,
      max: this.quantityInput.dataset.max
        ? parseInt(this.quantityInput.dataset.max)
        : null,
      step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.quantityInput.min = min;

    if (max) {
      this.quantityInput.max = max;
    } else {
      this.quantityInput.removeAttribute("max");
    }
    this.quantityInput.value = min;

    publish(THEME_EVENTS.quantityUpdate, undefined);
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    if (!this.isConnected) return;

    this.querySelector(
      ".quantity__rules-cart .loading__spinner",
    )?.classList.remove("hidden");
    fetch(
      `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((responseText) => {
        // 🔒 Si le composant a été rerender entre-temps → on stop
        if (!this.isConnected) return;
        const html = new DOMParser().parseFromString(responseText, "text/html");

        console.log("[PDP TEST] fetched html", {
          hasProductInfo: !!html.querySelector("product-info"),
          hasVariantSelects: !!html.querySelector("variant-selects"),
          hasSelectedVariant: !!html.querySelector(
            "variant-selects [data-selected-variant]",
          ),
          selectedVariantRaw: html.querySelector(
            "variant-selects [data-selected-variant]",
          )?.innerHTML,
          formInputValue: html.querySelector(
            'form[action*="/cart/add"] input[name="id"]',
          )?.value,
        });
        this.updateQuantityRules(this.dataset.sectionId, html);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (!this.isConnected) return;
        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        )?.classList.add("hidden");
      });
  }

  updateQuantityRules(sectionId, html) {
    if (!this.isConnected) return;
    if (!this.quantityInput || !this.quantityForm) return;

    this.setQuantityBoundries();

    const quantityFormUpdated = html.getElementById(
      `Quantity-Form-${sectionId}`,
    );
    const selectors = [
      ".quantity__input",
      ".quantity__rules",
      ".quantity__label",
    ];
    for (let selector of selectors) {
      const current = this.quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;
      if (selector === ".quantity__input") {
        const attributes = [
          "data-cart-quantity",
          "data-min",
          "data-max",
          "step",
        ];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  get productForm() {
    return this.querySelector(`product-form`);
  }

  get productModal() {
    return document.querySelector(`#ProductModal-${this.dataset.sectionId}`);
  }

  get pickupAvailability() {
    return this.querySelector(`pickup-availability`);
  }

  get variantSelectors() {
    return this.querySelector("variant-selects");
  }

  get relatedProducts() {
    const relatedProductsSectionId = SectionId.getIdForSection(
      SectionId.parseId(this.sectionId),
      "related-products",
    );
    return document.querySelector(
      `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
    );
  }

  get quickOrderList() {
    const quickOrderListSectionId = SectionId.getIdForSection(
      SectionId.parseId(this.sectionId),
      "quick_order_list",
    );
    return document.querySelector(
      `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
    );
  }

  get sectionId() {
    return this.dataset.originalSection || this.dataset.sectionId;
  }
}
if (!customElements.get("product-info")) {
  customElements.define("product-info", ProductInfo);
}
